import React, { useState, useRef, useCallback } from 'react';
import api from '../api';
import { toast } from 'sonner';
import { Upload, X, FileText, Image as ImageIcon, Film, Music, Archive, Paperclip, Loader2 } from 'lucide-react';

/**
 * Reusable file upload button/dropzone. Fires POST /api/uploads with the given
 * `context` (e.g. homework, intro-video, profile-pic, receipt, thumbnail).
 *
 * Props
 * -----
 *  context     REQUIRED — categorises the upload for downstream lookups
 *  refId       OPTIONAL — links this file to another entity id (homework_id, etc.)
 *  accept      OPTIONAL — HTML accept attr (e.g. "image/*,application/pdf")
 *  multiple    OPTIONAL — allow multi-select
 *  maxSizeMB   OPTIONAL — client-side size cap in MB (server enforces its own)
 *  onUploaded  callback({id, url, filename, size, content_type}) fires on success
 *  label       button text override
 *  variant     'button' (default) | 'dropzone' | 'compact'
 *  dataTestid  base testid; child elements get suffixes
 */
export default function FileUpload({
  context, refId = null, accept, multiple = false, maxSizeMB = 25,
  onUploaded, label = 'Upload', variant = 'button', dataTestid = 'file-upload',
}) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const doUpload = useCallback(async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    setUploading(true);
    try {
      for (const f of files) {
        if (f.size > maxSizeMB * 1024 * 1024) {
          toast.error(`${f.name} exceeds ${maxSizeMB} MB`);
          continue;
        }
        const form = new FormData();
        form.append('file', f);
        form.append('context', context);
        if (refId) form.append('ref_id', refId);
        const res = await api.post('/uploads', form, {
          onUploadProgress: e => {
            if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
          },
        });
        toast.success(`Uploaded ${f.name}`);
        if (onUploaded) onUploaded(res.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setProgress(0);
      if (inputRef.current) inputRef.current.value = '';
    }
  }, [context, refId, maxSizeMB, onUploaded]);

  const onChange = (e) => doUpload(e.target.files);
  const onDrop = (e) => {
    e.preventDefault(); setDragActive(false);
    doUpload(e.dataTransfer.files);
  };

  if (variant === 'dropzone') {
    return (
      <div
        onDragOver={e => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition ${dragActive ? 'border-indigo-500 bg-indigo-50' : 'border-slate-300 hover:border-indigo-400 hover:bg-slate-50'}`}
        data-testid={dataTestid}
      >
        <input ref={inputRef} type="file" accept={accept} multiple={multiple} onChange={onChange} className="hidden" data-testid={`${dataTestid}-input`} />
        {uploading ? (
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
            <p className="text-xs text-slate-600 font-semibold">Uploading… {progress}%</p>
            <div className="w-40 h-1.5 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-indigo-500 transition-all" style={{ width: `${progress}%` }} />
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1.5">
            <Upload className={`w-6 h-6 ${dragActive ? 'text-indigo-600' : 'text-slate-400'}`} />
            <p className="text-sm font-bold text-slate-700">{label}</p>
            <p className="text-[11px] text-slate-500">Drag & drop or click · max {maxSizeMB} MB</p>
          </div>
        )}
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <>
        <input ref={inputRef} type="file" accept={accept} multiple={multiple} onChange={onChange} className="hidden" data-testid={`${dataTestid}-input`} />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-slate-200 text-xs font-semibold text-slate-700 hover:border-indigo-400 hover:bg-slate-50 transition disabled:opacity-50"
          data-testid={dataTestid}
        >
          {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />}
          {uploading ? `${progress}%` : label}
        </button>
      </>
    );
  }

  return (
    <>
      <input ref={inputRef} type="file" accept={accept} multiple={multiple} onChange={onChange} className="hidden" data-testid={`${dataTestid}-input`} />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold disabled:opacity-50"
        data-testid={dataTestid}
      >
        {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
        {uploading ? `Uploading ${progress}%` : label}
      </button>
    </>
  );
}


/** Little file-row renderer that turns a metadata object into a chip w/ delete. */
export function FileChip({ file, onDelete, showDelete = true, dataTestid = 'file-chip' }) {
  const { API } = require('../api');           // import token attach helper is fine
  const token = localStorage.getItem('token');
  const isImg = (file.content_type || '').startsWith('image/');
  const Icon = pickIcon(file.content_type);
  const src = file.url && token ? `${process.env.REACT_APP_BACKEND_URL || ''}${file.url}?auth=${token}` : file.url;
  return (
    <div className="inline-flex items-center gap-2 px-2 py-1 rounded-md bg-slate-100 border border-slate-200 text-xs" data-testid={`${dataTestid}-${file.id}`}>
      {isImg ? (
        <img src={src} alt={file.filename} className="w-8 h-8 rounded object-cover" />
      ) : (
        <Icon className="w-4 h-4 text-slate-500" />
      )}
      <a href={src} target="_blank" rel="noopener noreferrer" className="font-semibold text-slate-700 hover:text-indigo-700 truncate max-w-[160px]">{file.filename || file.original_filename}</a>
      {showDelete && onDelete && (
        <button onClick={() => onDelete(file.id)} className="text-slate-400 hover:text-red-500" title="Remove" data-testid={`${dataTestid}-del-${file.id}`}>
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

function pickIcon(mime = '') {
  if (mime.startsWith('image/')) return ImageIcon;
  if (mime.startsWith('video/')) return Film;
  if (mime.startsWith('audio/')) return Music;
  if (mime.includes('zip') || mime.includes('rar') || mime.includes('7z')) return Archive;
  return FileText;
}
